import { useEffect, useRef, useState } from "react";

import { type AppUser } from "@/lib/types";

interface ProfileBadgeProps {
  user: AppUser;
  onSignOut: () => void;
}

export const ProfileBadge = ({ user, onSignOut }: ProfileBadgeProps) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocumentClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocumentClick);
    return () => document.removeEventListener("mousedown", onDocumentClick);
  }, [open]);

  const initials = initialsFor(user);
  const fullName = [user.givenName, user.familyName].filter(Boolean).join(" ") || user.email;

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-label="Uživatelské menu"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex h-10 w-10 items-center justify-center rounded-full bg-[#1a2b6b] text-sm font-semibold text-white shadow-md hover:bg-[#2a3b7b] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f6bff]"
      >
        {initials}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-12 z-50 min-w-[240px] rounded-lg border border-gray-200 bg-white p-4 shadow-lg"
        >
          <div className="text-sm font-semibold text-gray-900">{fullName}</div>
          <div className="mt-0.5 mb-3 text-xs break-all text-gray-500">{user.email}</div>
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onSignOut();
            }}
            className="w-full rounded-md bg-[#1a2b6b] p-2 text-sm text-white hover:opacity-80"
          >
            Odhlásit se
          </button>
        </div>
      )}
    </div>
  );
};

const initialsFor = (user: AppUser): string => {
  const first = user.givenName?.trim()?.[0];
  const last = user.familyName?.trim()?.[0];
  if (first && last) return (first + last).toUpperCase();
  if (first) return first.toUpperCase();
  const local = user.email?.split("@")[0] ?? "";
  return local.slice(0, 2).toUpperCase() || "?";
};
